import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Copy, UserCog } from 'lucide-react'
import { PRODUCT_SCOPES } from '@retink/types'
import { api } from '../../lib/api'
import { useMutation } from '@retink/web-ui'
import { Button } from '@retink/web-ui'
import { Badge } from '@retink/web-ui'
import { Input, Field } from '@retink/web-ui'
import { Card, CardHeader } from '@retink/web-ui'
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@retink/web-ui'
import { ConfirmDestructive } from '@retink/web-ui'
import { ConfirmAction } from '@retink/web-ui'
import { RowActions, DropdownMenuContent, DropdownMenuItem } from '@retink/web-ui'
import { PageHeader } from '@retink/web-ui'
import { EmptyState } from '@retink/web-ui'
import { FormError } from '@retink/web-ui'
import { toast } from '@retink/web-ui'

interface OrgAdmin { id: string; email: string; display_name: string | null; status: string }
interface OrgTenant { id: string; name: string }
interface OrgInvite {
  id: string; email: string; org_role: string | null; tenant_role: string | null
  cc_role: string | null; tenant_name: string | null; status: string; expires_at: string
}

const NONE = '__none'
// Display labels for the shared Axis-B vocabulary (labels are a UI concern).
const CC_LABEL: Record<string, string> = { agent: 'Agent', supervisor: 'Supervisor', admin: 'Admin' }

// Org-level people: org admins (the customer's super-admins) and multi-level
// invites. Staff-only in Pass 1 (reads /api/admin/orgs/:id/*).
export function OrgUsers() {
  const { orgId } = useParams<{ orgId: string }>()
  const [admins, setAdmins] = useState<OrgAdmin[]>([])
  const [adminEmail, setAdminEmail] = useState('')

  const [orgTenants, setOrgTenants] = useState<OrgTenant[]>([])
  const [invites, setInvites] = useState<OrgInvite[]>([])
  const [inv, setInv] = useState({ email: '', orgRole: '', tenantId: '', tenantRole: 'user', ccScope: '' })
  const [inviteLink, setInviteLink] = useState<{ email: string; url: string } | null>(null)
  // Org admin awaiting type-to-confirm removal.
  const [removing, setRemoving] = useState<OrgAdmin | null>(null)
  // Invite awaiting one-click revoke confirmation.
  const [revoking, setRevoking] = useState<OrgInvite | null>(null)

  const loadAdmins = () => api.get<OrgAdmin[]>(`/api/orgs/${orgId}/admins`).then(setAdmins).catch(() => setAdmins([]))
  const loadTenants = () => api.get<OrgTenant[]>(`/api/orgs/${orgId}/tenants`).then(setOrgTenants).catch(() => setOrgTenants([]))
  const loadInvites = () => api.get<OrgInvite[]>(`/api/orgs/${orgId}/invites`).then(setInvites).catch(() => setInvites([]))
  useEffect(() => { void loadAdmins(); void loadTenants(); void loadInvites() }, [orgId])

  const addAdminM = useMutation(() => api.post(`/api/orgs/${orgId}/admins`, { email: adminEmail }), { fallbackError: 'Failed' })
  async function addAdmin(e: React.FormEvent) {
    e.preventDefault()
    if (await addAdminM.mutate() === undefined) return
    setAdminEmail('')
    await loadAdmins()
  }

  async function removeAdmin(userId: string) {
    await api.delete(`/api/orgs/${orgId}/admins/${userId}`).catch(() => {})
    await loadAdmins()
  }

  const inviteM = useMutation(() => {
    const body: Record<string, unknown> = { email: inv.email }
    if (inv.orgRole) body['orgRole'] = inv.orgRole
    if (inv.tenantId) {
      body['tenantId'] = inv.tenantId
      body['tenantRole'] = inv.tenantRole
      if (inv.ccScope) body['ccScope'] = inv.ccScope
    }
    return api.post<{ added?: boolean; inviteUrl?: string }>(`/api/orgs/${orgId}/invites`, body)
  }, { fallbackError: 'Failed to invite' })
  async function sendInvite(e: React.FormEvent) {
    e.preventDefault()
    const email = inv.email
    const res = await inviteM.mutate()
    if (res === undefined) return
    setInv({ email: '', orgRole: '', tenantId: '', tenantRole: 'user', ccScope: '' })
    await loadInvites()
    if (res.added) {
      toast.success(`${email} added`)
    } else if (res.inviteUrl) {
      setInviteLink({ email, url: res.inviteUrl })
      let copied = false
      if (navigator.clipboard) {
        copied = await navigator.clipboard.writeText(res.inviteUrl).then(() => true).catch(() => false)
      }
      toast.success(copied ? 'Invite created — claim link copied' : 'Invite created — copy the claim link below')
    } else {
      toast.success('Invite created')
    }
  }

  async function revokeInvite(id: string) {
    await api.post(`/api/orgs/${orgId}/invites/${id}/revoke`, {}).catch(() => {})
    await loadInvites()
  }

  return (
    <div className="p-8 max-w-3xl">
      <PageHeader
        title="People"
        description="Organization administrators and people assigned to its workspaces."
      />

      <Card className="mb-6">
        <CardHeader>Organization admins</CardHeader>
        <div className="px-4 py-4">
          <p className="mb-3 text-xs text-muted-foreground">
            Organization admins manage identity, people, and every workspace in this organization.
          </p>
          <form onSubmit={addAdmin} className="flex gap-2 mb-2">
            <Input type="email" value={adminEmail} required placeholder="existing user's email"
              onChange={(e) => setAdminEmail(e.target.value)} />
            <Button type="submit" variant="secondary" disabled={addAdminM.saving}>Add existing</Button>
          </form>
          <FormError className="mb-2">{addAdminM.error}</FormError>
          <div className="divide-y divide-border/60 rounded-lg border border-border">
            {admins.map((a) => (
              <div key={a.id} className="flex items-center justify-between px-3 py-2 text-sm">
                <span className="flex items-center gap-2">
                  {a.email}
                  {a.status !== 'active' && <Badge tone="warning">{a.status}</Badge>}
                </span>
                <RowActions label={`Actions for ${a.email}`}>
                  <DropdownMenuContent>
                    <DropdownMenuItem destructive onSelect={() => setRemoving(a)}>Remove org admin</DropdownMenuItem>
                  </DropdownMenuContent>
                </RowActions>
              </div>
            ))}
            {admins.length === 0 && (
              <EmptyState
                icon={UserCog}
                title="No org admins yet"
                description="Add an existing user by email to let them administer every workspace in this organization."
                className="py-8"
              />
            )}
          </div>
        </div>
      </Card>

      <Card>
        <CardHeader>Invite a person</CardHeader>
        <div className="px-4 py-4">
          <form onSubmit={sendInvite} className="space-y-3">
            <Field label="Email">
              <Input type="email" value={inv.email} required placeholder="person@company.com"
                onChange={(e) => setInv({ ...inv, email: e.target.value })} />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Organization role">
                <Select value={inv.orgRole || NONE} onValueChange={(v) => setInv({ ...inv, orgRole: v === NONE ? '' : v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>No organization access</SelectItem>
                    <SelectItem value="admin">Administrator</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Also add to workspace (optional)">
                <Select value={inv.tenantId || NONE} onValueChange={(v) => setInv({ ...inv, tenantId: v === NONE ? '' : v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>No workspace</SelectItem>
                    {orgTenants.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
            </div>
            {inv.tenantId && (
              <div className="grid grid-cols-2 gap-3">
                <Field label="Workspace role">
                  <Select value={inv.tenantRole} onValueChange={(v) => setInv({ ...inv, tenantRole: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="user">User</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Call-centre role">
                  <Select value={inv.ccScope || NONE} onValueChange={(v) => setInv({ ...inv, ccScope: v === NONE ? '' : v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>None</SelectItem>
                      {PRODUCT_SCOPES.callcenter.map((s) => (
                        <SelectItem key={s} value={s}>{CC_LABEL[s] ?? s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              </div>
            )}
            <FormError>{inviteM.error}</FormError>
            <Button type="submit" disabled={inviteM.saving || (!inv.orgRole && !inv.tenantId)}>
              {inviteM.saving ? 'Creating…' : 'Create invite link'}
            </Button>
          </form>

          {inviteLink && (
            <div className="mt-4 space-y-2 rounded-lg border border-primary/40 p-3">
              <p className="text-sm">Claim link for <span className="font-medium">{inviteLink.email}</span></p>
              <div className="flex gap-2">
                <Input value={inviteLink.url} readOnly aria-label="Invite claim URL" className="font-mono text-xs" />
                <Button type="button" variant="outline" onClick={() => void navigator.clipboard?.writeText(inviteLink.url).then(() => toast.success('Invite link copied'))}>
                  <Copy /> Copy
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">Send this one-time link to the person. Email delivery is not connected yet.</p>
            </div>
          )}

          {invites.length > 0 && (
            <div className="mt-4 rounded-lg border border-border divide-y divide-border/60">
              {invites.map((i) => (
                <div key={i.id} className="flex items-center justify-between px-3 py-2 text-sm">
                  <span className="flex items-center gap-2 flex-wrap">
                    {i.email}
                    {i.org_role && <Badge tone="neutral">org:{i.org_role}</Badge>}
                    {i.tenant_role && <Badge tone="neutral">{i.tenant_name ?? 'workspace'}:{i.tenant_role}</Badge>}
                    {i.cc_role && <Badge tone="neutral">cc:{i.cc_role}</Badge>}
                  </span>
                  <RowActions label={`Actions for invite to ${i.email}`}>
                    <DropdownMenuContent>
                      <DropdownMenuItem destructive onSelect={() => setRevoking(i)}>Revoke invite</DropdownMenuItem>
                    </DropdownMenuContent>
                  </RowActions>
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>

      {/* Remove org admin — guarded by typing the admin's email */}
      <ConfirmDestructive
        open={removing !== null}
        onOpenChange={(o) => { if (!o) setRemoving(null) }}
        title={`Remove ${removing?.email}?`}
        confirmPhrase={removing?.email ?? ''}
        confirmLabel="Remove org admin"
        onConfirm={async () => { if (removing) await removeAdmin(removing.id) }}
        description={`Revokes ${removing?.email}'s admin access to every workspace in this organization. Re-add them to restore it.`}
      />

      {/* Revoke a pending invite — one-click confirm */}
      <ConfirmAction
        open={revoking !== null}
        onOpenChange={(o) => { if (!o) setRevoking(null) }}
        title={`Revoke the invite to ${revoking?.email}?`}
        confirmLabel="Revoke invite"
        onConfirm={async () => { if (revoking) await revokeInvite(revoking.id) }}
        description="The invite link stops working. You can send a new invite later."
      />
    </div>
  )
}
